import React, { ReactElement, ReactNode, useEffect } from 'react'
import styled from 'styled-components'
import NavbarController from '../state/NavbarController'
import { RADOM_COLORS } from '../util/Constants'
import { IconButton } from './Button'
import Close from '../icons/Close'

const DrawerOverlay = styled.div`
  animation: drawerOverlayFadeIn 0.18s ease;
  backdrop-filter: blur(3px);
  background: rgba(17, 24, 39, 0.28);
  inset: 0;
  position: fixed;
  z-index: 101;

  @keyframes drawerOverlayFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`

const DrawerPanel = styled.div<{ $width: number }>`
  animation: drawerPanelSlideIn 0.22s ease;
  background: var(--surface-subtle, #fbfcfe);
  border-left: 1px solid var(--border, #e5ebf3);
  box-shadow: -18px 0 46px rgba(32, 36, 45, 0.14);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  position: fixed;
  right: 0;
  top: 0;
  width: ${({ $width }) => `min(100%, ${$width}px)`};
  z-index: 102;

  @keyframes drawerPanelSlideIn {
    from { transform: translateX(105%); }
    to { transform: translateX(0); }
  }
`

const DrawerHeader = styled.div`
  align-items: flex-start;
  background: var(--surface, #ffffff);
  border-bottom: 1px solid var(--surface-pressed, #edf1f7);
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 22px 24px 18px;
`

const DrawerTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`

const DrawerEyebrow = styled.span`
  color: var(--text-faint, #7b8494);
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 1.35;
  text-transform: uppercase;
`

const DrawerTitle = styled.span`
  color: ${RADOM_COLORS.BLACK};
  font-size: 19px;
  font-weight: 500;
  line-height: 1.25;
  overflow-wrap: anywhere;
`

const DrawerSubtitle = styled.div`
  color: var(--text-faint, #7b8494);
  font-size: 12.5px;
  line-height: 1.35;
  min-width: 0;
  overflow-wrap: anywhere;
`

const DrawerBody = styled.div`
  background: var(--surface-subtle, #fbfcfe);
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
  overflow-y: auto;
  padding: 18px 24px 24px;
`

export const DrawerCard = styled.div`
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e8edf5);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(32, 36, 45, 0.03);
  display: flex;
  flex-direction: column;
  padding: 16px;
`

export const DrawerDetailRow = styled.div`
  align-items: flex-start;
  border-top: 1px solid var(--surface-pressed, #edf1f7);
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 42px;
  min-width: 0;
  padding: 10px 0;

  &:first-child {
    border-top: 0;
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
`

export const DrawerDetailLabel = styled.span`
  color: var(--text-faint, #7b8494);
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
`

export const DrawerDetailValue = styled.span`
  color: var(--ink, #20242d);
  align-items: flex-end;
  display: inline-flex;
  flex: 1 1 auto;
  flex-direction: column;
  font-size: 13px;
  font-weight: 500;
  justify-content: flex-end;
  line-height: 1.35;
  max-width: 72%;
  min-width: 0;
  overflow-wrap: anywhere;
  text-align: right;
`

interface DetailsDrawerProps {
  visible: boolean
  title: ReactNode
  children: ReactNode
  onClose: () => void
  eyebrow?: ReactNode
  subtitle?: ReactNode
  width?: number
  ariaLabel?: string
}

const DetailsDrawer = ({
  visible,
  title,
  children,
  onClose,
  eyebrow,
  subtitle,
  width = 520,
  ariaLabel
}: DetailsDrawerProps): ReactElement | null => {
  useEffect(() => {
    NavbarController.setModalOpen(visible)
    return () => NavbarController.setModalOpen(false)
  }, [visible])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && visible) {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, visible])

  if (!visible) {
    return null
  }

  return <>
    <DrawerOverlay onClick={onClose} />
    <DrawerPanel role='dialog' aria-modal='true' aria-label={ariaLabel} $width={width}>
      <DrawerHeader>
        <DrawerTitleBlock>
          {eyebrow && <DrawerEyebrow>{eyebrow}</DrawerEyebrow>}
          <DrawerTitle>{title}</DrawerTitle>
          {subtitle && <DrawerSubtitle>{subtitle}</DrawerSubtitle>}
        </DrawerTitleBlock>
        <IconButton
          type='button'
          onClick={onClose}
          style={{
            alignItems: 'center',
            background: 'var(--surface-hover, #f7f9fc)',
            border: '1px solid var(--border, #e5ebf3)',
            borderRadius: 8,
            display: 'flex',
            height: 34,
            justifyContent: 'center',
            padding: 0,
            width: 34
          }}>
          <Close width={16} height={16} />
        </IconButton>
      </DrawerHeader>
      <DrawerBody>
        {children}
      </DrawerBody>
    </DrawerPanel>
  </>
}

export default DetailsDrawer